import { useInfiniteQuery } from "@tanstack/react-query";
import { listSitesInfiniteOptions } from "../../services/client/@tanstack/react-query.gen";
import type { ColumnsType } from "antd/es/table";
import Table from "antd/es/table";
import {
  IngressStatusOutput,
  SiteConfigurationStatusOutput,
  SiteReadable as Site,
} from "../../services/client/types.gen";
import { Alert, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import { LinkButton } from "../LinkButton";
import ValidationStatusBadge from "../util-components/ValidationStatusBadge";
import { siteStatusTag } from "../util-components/siteStatusTag";
import {
  FETCH_PAGE_SIZE,
  useCursorPagination,
} from "../../hooks/useCursorPagination";
import { formatDateTime } from "../../util/formatDate";

export const ListSites = () => {
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...listSitesInfiniteOptions({
      query: { page_size: FETCH_PAGE_SIZE },
    }),
    getNextPageParam: (lastPage) => lastPage.end_cursor,
  });

  const navigate = useNavigate();

  const { dataSource, pagination, isPageLoading } = useCursorPagination<Site>({
    pages: data?.pages,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  });

  const columns: ColumnsType<Site> = [
    { title: "Site ID", dataIndex: "site_id", key: "site_id" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status?: Site["status"]) => {
        const { title, color } = siteStatusTag[status ?? "inactive"];
        return <Tag color={color}>{title}</Tag>;
      },
    },
    {
      title: "Ingress Status",
      dataIndex: ["validation_status", "ingress_status"],
      key: "ingress_status",
      render: (ingressStatus?: IngressStatusOutput) => (
        <ValidationStatusBadge
          status={ingressStatus?.status}
          label="Ingress Status"
          ingressStatus={ingressStatus}
        />
      ),
    },
    {
      title: "Configuration Status",
      dataIndex: ["validation_status", "site_configuration_status"],
      key: "configuration_status",
      render: (configStatus?: SiteConfigurationStatusOutput) => (
        <ValidationStatusBadge
          status={configStatus?.status}
          label="Configuration Status"
          configurationStatus={configStatus}
        />
      ),
    },
    {
      title: "Last Validation",
      dataIndex: ["validation_status", "last_validation"],
      key: "last_validation",
      render: (lastValidation?: string) =>
        lastValidation ? formatDateTime(lastValidation) : "N/A",
    },
    {
      title: "",
      render: (_, record) => (
        <LinkButton
          to={`/sites/${record.site_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          Details
        </LinkButton>
      ),
    },
  ];

  return (
    <>
      {error && (
        <Alert
          message="Error"
          description="There was an issue loading the sites data."
          type="error"
          showIcon
        />
      )}
      <Table<Site>
        columns={columns}
        dataSource={dataSource}
        loading={isLoading || isPageLoading}
        pagination={pagination}
        onRow={(record) => {
          return {
            onClick: () => {
              navigate(`/sites/${record.site_id}`);
            },
          };
        }}
        rowKey="site_id"
      />
    </>
  );
};
